import formatDateKey from './formatDateKey'

/**
 * Hoe de facturatie haar eenheden, redenen en aantallen aan de gebruiker
 * toont. Eén plek, zodat scherm en csv-export hetzelfde zeggen.
 * AI-CONTEXT: See docs/pitfalls.md#ui-export-claims-everything-on-screen
 */

/** Zelfde woorden als het keuzemenu bij een traject (Instellingen → Ondersteuning). */
const UNIT_LABELS = {
    hour: 'Uur',
    halfday: 'Dagdeel',
    day: 'Dag',
    week: 'Week',
    month: 'Maand',
    year: 'Jaar',
}

/** Eén naam per reden uit `computeBilling`. */
const SKIP_REASON_LABELS = {
    noRoute: 'Geen traject',
    unitNotBillable: 'Eenheid niet te berekenen',
    noTariff: 'Geen tarief',
    duplicateReport: 'Twee registraties op één dag',
    noHours: 'Geen leesbare begin- of eindtijd',
    noDate: 'Registratie zonder datum',
    unknownPersona: 'Deelnemer bestaat niet meer',
}

/** @param {string} unit @returns {string} */
const unitLabel = (unit) => UNIT_LABELS[unit] || 'Geen eenheid'

/** @param {string} reason @returns {string} De naam, of de reden zelf als die onbekend is */
const skipReasonLabel = (reason) => SKIP_REASON_LABELS[reason] || reason

/**
 * @param {number} quantity
 * @returns {string} Met komma, hooguit twee decimalen, zonder overbodige nullen
 */
const formatQuantity = (quantity) => String(Math.round(quantity * 100) / 100).replace('.', ',')

/**
 * Waarom een deelnemer (deels) niet te berekenen is, met de dagen erbij.
 * @param {import('./computeBilling').SkippedEntry} entry
 * @returns {string} bijv. 'Eenheid niet te berekenen (Maand): 03-09-2026'
 */
const describeSkipped = (entry) => {
    const detail = entry.detail ? ` (${unitLabel(entry.detail)})` : ''
    const dates = entry.dates.length > 0 ? `: ${entry.dates.map(formatDateKey).join(', ')}` : ''
    const extra = entry.adjustmentIds.length > 0 ? ` · ${adjustmentCountText(entry.adjustmentIds.length)}` : ''
    return `${skipReasonLabel(entry.reason)}${detail}${dates}${extra}`
}

/** @param {number} count @returns {string} '1 extra regel' of '2 extra regels' */
const adjustmentCountText = (count) => `${count} extra ${count === 1 ? 'regel' : 'regels'}`

/**
 * Waarom een extra regel voor deze deelnemer niet meetelt, en wat er nodig is.
 * @param {{reason: string, detail: string}} blocker - Uit `adjustmentBlocker`
 * @param {string} name - Naam van de deelnemer
 * @returns {string}
 */
const adjustmentBlockerText = (blocker, name) => {
    if (blocker.reason === 'noRoute') return `${name} heeft geen traject; deze regel telt pas mee als er een traject is.`
    if (blocker.reason === 'unitNotBillable') {
        return `Het traject van ${name} rekent per ${unitLabel(blocker.detail).toLowerCase()}; een extra regel telt alleen bij een traject per dagdeel, uur of dag.`
    }
    if (blocker.reason === 'noTariff') return `${name} heeft op de laatste dag van de maand geen tarief; deze regel telt pas mee als er een tarief is.`
    return `Deze regel telt niet mee: ${skipReasonLabel(blocker.reason).toLowerCase()}.`
}

/**
 * De zin bij het totaal over wie er niet in zit; leeg als iedereen erin zit.
 * AI-CONTEXT: See docs/pitfalls.md#ui-aggregate-collapses-nothing-into-zero
 * @param {number} count - `skippedPersonaCount`
 * @returns {string}
 */
const skippedSummary = (count) => {
    if (count <= 0) return ''
    return count === 1
        ? 'zonder 1 deelnemer die (deels) niet te berekenen is'
        : `zonder ${count} deelnemers die (deels) niet te berekenen zijn`
}

/** Enkelvoud en meervoud per eenheid waarin de facturatie telt. */
const UNIT_COUNT_WORDS = {
    halfday: ['dagdeel', 'dagdelen'],
    day: ['dag', 'dagen'],
    hour: ['uur', 'uur'],
}

/**
 * De aantallen van een reeks regels, per eenheid opgeteld — nooit over
 * eenheden heen, want 4 dagdelen en 3 uur zijn geen 7 van iets.
 * @param {Array<{unit: string, quantity: number}>} lines
 * @returns {string} bijv. '4 dagdelen, 3,5 uur'; '' zonder regels
 */
const summarizeQuantities = (lines) => {
    /** @type {Map<string, number>} */
    const totals = new Map()
    for (const line of lines) totals.set(line.unit, (totals.get(line.unit) || 0) + line.quantity)
    return [...totals.entries()].map(([unit, total]) => {
        const [one, many] = UNIT_COUNT_WORDS[unit] || [unitLabel(unit).toLowerCase(), unitLabel(unit).toLowerCase()]
        return `${formatQuantity(total)} ${Math.round(total * 100) === 100 ? one : many}`
    }).join(', ')
}

/**
 * De overgeslagen deelnemers per reden, in de volgorde van `SKIP_REASON_LABELS`.
 * @param {Array<import('./computeBilling').SkippedEntry>} skipped
 * @returns {Array<{reason: string, label: string, entries: Array<import('./computeBilling').SkippedEntry>}>}
 */
const groupSkippedByReason = (skipped) => {
    const order = Object.keys(SKIP_REASON_LABELS)
    const rank = (reason) => (order.includes(reason) ? order.indexOf(reason) : order.length)
    const reasons = [...new Set(skipped.map((s) => s.reason))].sort((a, b) => rank(a) - rank(b))
    return reasons.map((reason) => ({
        reason,
        label: skipReasonLabel(reason),
        entries: skipped.filter((s) => s.reason === reason),
    }))
}

export { unitLabel, skipReasonLabel, formatQuantity, describeSkipped, adjustmentCountText, adjustmentBlockerText, skippedSummary, summarizeQuantities, groupSkippedByReason, UNIT_LABELS, SKIP_REASON_LABELS }
